import React, { useEffect } from "react";
import {
  XMarkIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

const toneMap = {
  success: {
    icon: CheckCircleIcon,
    bg: "tw-bg-emerald-600",
    text: "tw-text-white",
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bg: "tw-bg-amber-500",
    text: "tw-text-gray-900",
  },
  // Failure toasts. Callers across the app already pass tone="error"
  // (Google + M365 admin tools, etc.); without this entry they fell back
  // to the blue "info" styling. "danger" is accepted as an alias.
  error: {
    icon: XCircleIcon,
    bg: "tw-bg-red-600",
    text: "tw-text-white",
  },
  danger: {
    icon: XCircleIcon,
    bg: "tw-bg-red-600",
    text: "tw-text-white",
  },
  info: {
    icon: InformationCircleIcon,
    bg: "tw-bg-blue-600",
    text: "tw-text-white",
  },
};

const ToastNotification = ({
  isOpen,
  title,
  message,
  tone = "info",
  autoHideDuration = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen || !autoHideDuration) return undefined;
    const timer = setTimeout(() => {
      onClose?.();
    }, autoHideDuration);
    return () => clearTimeout(timer);
  }, [isOpen, autoHideDuration, onClose]);

  if (!isOpen) {
    return null;
  }

  const config = toneMap[tone] || toneMap.info;
  const Icon = config.icon;

  return (
    <div className="tw-fixed tw-top-6 tw-right-6 tw-z-40 tw-flex tw-max-w-md tw-items-start tw-gap-3">
      <div
        className={`tw-flex-1 tw-rounded-2xl tw-shadow-2xl tw-ring-1 tw-ring-black/10 dark:tw-ring-white/10 ${config.bg} tw-backdrop-blur-sm tw-bg-opacity-95`}
      >
        <div className="tw-flex tw-items-start tw-gap-3 tw-px-5 tw-py-4">
          <div className="tw-flex-shrink-0">
            <Icon className={`tw-h-6 tw-w-6 ${config.text}`} />
          </div>
          <div className="tw-flex-1">
            {title && (
              <p className={`tw-text-sm tw-font-semibold ${config.text}`}>
                {title}
              </p>
            )}
            {message && (
              <p className={`tw-mt-0.5 tw-text-sm ${config.text} tw-opacity-90`}>
                {message}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close notification"
            className="tw-rounded-full tw-p-1 tw-text-white/70 hover:tw-bg-white/20 hover:tw-text-white"
          >
            <XMarkIcon className="tw-h-4 tw-w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToastNotification;
